import { Fragment, useState, useEffect, useCallback, useMemo } from 'react'
import {
  Badge,
  Inline,
  Stack,
  Text
} from '@leroy-merlin-br/backyard-react-ui'

import emitter from 'garden/src/js/utils/emitter'

import { pushDataObject, setProductView } from 'scripts/utils/data-layer'

import { cheapestModalityOption } from 'catalog/utils/cheapest-modality-option'
import { SHIPMENTS_ERROR_STATUS } from 'catalog/utils/constants'
import * as S from 'catalog/pages/Product/containers/Buybox/styled'

import { OptionInput } from './components/OptionInput'
import { ShipmentPrice } from './components/ShipmentPrice'
import { SellersListButton } from './components/SellersListButton'
import { SellersList } from './components/SellersList'

const Buybox = ({
  isAssistedSale,
  limitQuantity,
  regionId,
  skus: propsSkus,
  product: propsProduct,
  selectedSeller: propsSelectedSeller,
  isPostalCodeEnabled
}) => {
  const [selectedSeller, setSelectedSeller] = useState(JSON.parse(propsSelectedSeller))
  const [shipments, setShipments] = useState([])
  const [shipmentsErrorStatus, setShipmentsErrorStatus] = useState(0)

  const skus = useMemo(() => JSON.parse(propsSkus), [propsSkus])
  const product = useMemo(() => JSON.parse(propsProduct), [propsProduct])
  const showButtonCondition = useMemo(() => skus.length > 3, [skus])
  const shouldRenderDefaultShipment = useMemo(() => {
    const differentRegionError = shipmentsErrorStatus === SHIPMENTS_ERROR_STATUS.differentRegion
    return !shipments?.length && (!shipmentsErrorStatus || differentRegionError)
  }, [shipments, shipmentsErrorStatus])

  const getInitialShipmentMessage = useCallback(
    (skuId) => {
      const sku = skus.find((item) => item.id === skuId)
      if (sku.isFreeShipping && !isAssistedSale) {
        return <Badge color="green">Frete grátis</Badge>
      }

      return '+ frete'
    },
    [isAssistedSale, skus]
  )

  const handleSellerSelect = (e) => {
    emitter.emit('buybox:sellerChange', e.target.dataset)

    const parsedSelectedSeller = JSON.parse(
      e.target?.dataset?.selectedSeller || '{}'
    )
    setSelectedSeller(parsedSelectedSeller)

    const dataObject = setProductView(e.target.dataset)
    pushDataObject(dataObject)
  }

  const getShipmentsInfoBySkuId = useCallback(
    (id) => {
      const skuId = id !== '' ? id : null
      return shipments.find((seller) => seller.skuId === skuId)
    },
    [shipments]
  )

  useEffect(() => {
    emitter.on('shipments:change', (data) => {
      setShipments(data.shipments)
      setShipmentsErrorStatus(data.shipmentsErrorStatus)
    })
  }, [])

  return (
    <Fragment>
      <S.Wrapper>
        <Stack spacing="bit">
          {limitQuantity && (
            <Text as="div" size="kilo" bold noMargin>
              {limitQuantity}
            </Text>
          )}

          <Stack spacing="bit">
            {skus.slice(0, 3).map((sku, index) => {
              const skuShipments = shipments ? getShipmentsInfoBySkuId(sku.id) : null
              const cheapestShipmentModality = skuShipments ? cheapestModalityOption(skuShipments.modalities) : null

              const hasShipment = !!cheapestShipmentModality?.modality

              return (
                <div key={sku.id}>
                  <OptionInput
                    id={`buybox_option_radio_${index}`}
                    product={product}
                    sku={sku}
                    selectedSeller={selectedSeller}
                    onChange={(e) => handleSellerSelect(e)}
                  />
                  <S.OptionLabel
                    htmlFor={`buybox_option_radio_${index}`}
                    isSelected={selectedSeller.id === sku.id}
                    hasShipment={hasShipment}
                    isPostalCodeEnabled={isPostalCodeEnabled}
                  >
                    <S.OptionPrice as="div" size="giga" bold noMargin>
                      <Stack spacing="bit">
                        <Inline
                          spacing="bit"
                          justify="left"
                          align="bottom"
                          noWrap
                        >
                          <S.OptionPriceValue>
                            R$ {sku.price.to.integers},{sku.price.to.decimals}
                          </S.OptionPriceValue>

                          <Text as="span" size="kilo" noMargin>
                            {product.unit}
                          </Text>
                        </Inline>

                        {isPostalCodeEnabled && (
                          <Text size="kilo" noMargin>
                            {shouldRenderDefaultShipment
                              ? (
                                  getInitialShipmentMessage(sku.id)
                                )
                              : (
                                  <ShipmentPrice
                                    modality={cheapestShipmentModality?.modality}
                                    cost={cheapestShipmentModality?.cost}
                                    deadline={cheapestShipmentModality?.deadline}
                                    errorStatus={shipmentsErrorStatus}
                                  />
                                )}
                          </Text>
                        )}

                      </Stack>
                    </S.OptionPrice>

                    <S.OptionSeller title={sku.shop.name} size="kilo" noMargin>
                      {sku.shop.name}
                    </S.OptionSeller>
                  </S.OptionLabel>
                </div>
              )
            })}
            {showButtonCondition && (
              <SellersListButton
                sellersListMinorPrice={`${skus[0].price.to.integers}, ${skus[0].price.to.decimals}`}
                sellersListUnit={product.unit}
              />
            )}
          </Stack>
        </Stack>
      </S.Wrapper>

      {showButtonCondition && (
        <SellersList
          productId={product.id}
          productName={product.name}
          productImg={product.img}
          productBrand={product.brand}
          regionId={regionId}
        />
      )}
    </Fragment>
  )
}

export default Buybox
